import { useCallback, useEffect, useMemo, useState } from 'react';

import { ErroRespostaHttp } from '../../../shared/api/http';
import { lerUltimoEnigma, salvarRelacaoUltimoEnigma } from '../api/conteudo';
import type { UltimoEnigma } from '../api/conteudo';
import { trocarCampo } from '../model/relacaoDasDicas';

function mensagemDoErro(erro: unknown): string {
    if (erro instanceof ErroRespostaHttp) {
        if (typeof erro.corpo?.mensagem === 'string') return erro.corpo.mensagem;
        if (erro.status === 401 || erro.status === 403) return 'Sessão expirada ou sem permissão. Entre de novo.';
    }
    return 'Não foi possível salvar. Tente novamente.';
}

const camposDe = (dados: UltimoEnigma) => dados.pistas.map((pista) => pista.campo);

/**
 * Scan Pixel (AP12): o admin escolhe o campo que cada PISTA (aba do tablet, contada da esquerda)
 * do último enigma preenche. Dar a uma PISTA um campo que já está em outra troca as duas. A
 * relação vale para todas as turmas e só muda sem grupos jogando o Scan Pixel.
 */
export function UltimoEnigmaPainel({ csrfToken }: { csrfToken: string }) {
    const [dados, setDados] = useState<UltimoEnigma | null>(null);
    const [campos, setCampos] = useState<number[]>([]);
    const [erro, setErro] = useState<string | null>(null);
    const [ocupado, setOcupado] = useState(false);

    const mostrar = useCallback((recebido: UltimoEnigma) => {
        setDados(recebido);
        setCampos(camposDe(recebido));
    }, []);

    const carregar = useCallback(async () => {
        try {
            const recebido = await lerUltimoEnigma();
            // Servidor antigo, sem esta rota, devolve outra coisa: trata como falha de carga.
            if (!Array.isArray(recebido?.pistas)) throw new Error('formato');
            mostrar(recebido);
        } catch (falha) {
            setErro(falha instanceof Error && falha.message === 'formato'
                ? 'Não foi possível carregar as dicas do último enigma.'
                : mensagemDoErro(falha));
        }
    }, [mostrar]);

    useEffect(() => {
        void carregar();
    }, [carregar]);

    const pistaDoCampo = useMemo(
        () => new Map((dados?.pistas ?? []).map((pista) => [pista.campo, pista])),
        [dados],
    );
    const opcoes = useMemo(
        () => [...(dados?.pistas ?? [])].sort((a, b) => a.campo - b.campo),
        [dados],
    );
    const alterado = dados !== null && campos.some((campo, i) => campo !== dados.pistas[i]?.campo);
    const travado = !dados?.editavel || ocupado;

    async function salvar() {
        if (!window.confirm('Salvar a relação entre as pistas e os campos do último enigma? Vale para todas as turmas.')) return;
        setErro(null);
        setOcupado(true);
        try {
            mostrar(await salvarRelacaoUltimoEnigma(campos, csrfToken));
        } catch (falha) {
            setErro(mensagemDoErro(falha));
        } finally {
            setOcupado(false);
        }
    }

    function desfazer() {
        if (!dados) return;
        setErro(null);
        setCampos(camposDe(dados));
    }

    return (
        <section className="grupo-variaveis" aria-label="Dicas do último enigma">
            <h3>Dicas do último enigma{dados?.enigma ? ` (Enigma ${dados.enigma})` : ''}</h3>
            {erro && <p className="mensagem-login" role="alert">{erro}</p>}
            {dados && (
                <>
                    {!dados.editavel && (
                        <p className="aviso-fase-aberta" role="status">
                            Há grupos jogando o Scan Pixel em {dados.turmas_com_fase_aberta.join(', ')}. Quando o
                            facilitador finalizar esses grupos, dá para alterar.
                        </p>
                    )}
                    <p className="motivo-estrutura">
                        Cada PISTA é uma aba do tablet, contada da esquerda. Escolha o campo que ela preenche; escolher
                        um campo que já está em outra PISTA troca as duas.
                    </p>
                    <ol className="relacao-dicas">
                        {campos.map((campo, i) => {
                            const id = `relacao-dica-${i + 1}`;
                            return (
                                <li key={id}>
                                    <label htmlFor={id}>PISTA {i + 1}</label>
                                    <select
                                        id={id}
                                        className="seletor-material-chave"
                                        value={campo}
                                        disabled={travado}
                                        onChange={(e) => setCampos((atuais) => trocarCampo(atuais, i, Number(e.target.value)))}
                                    >
                                        {opcoes.map((pista) => (
                                            <option key={pista.id} value={pista.campo}>
                                                Campo {pista.campo} · {pista.resposta}
                                            </option>
                                        ))}
                                    </select>
                                    <span className="enunciado-dica">{pistaDoCampo.get(campo)?.enunciado}</span>
                                </li>
                            );
                        })}
                    </ol>
                    <div className="acoes-grupo">
                        <button
                            className="botao-entrar" type="button"
                            disabled={travado || !alterado}
                            onClick={() => void salvar()}
                        >
                            Salvar
                        </button>
                        <button className="botao-texto" type="button" disabled={ocupado || !alterado} onClick={desfazer}>
                            Desfazer
                        </button>
                    </div>
                    <p className="nota-pontuacao">
                        A relação vale para todas as turmas. No tablet, a aba mostra PISTA N e a pista diz em qual campo vai a resposta.
                    </p>
                </>
            )}
        </section>
    );
}
